import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import { useToast } from '../../../hooks/useToast';
import { RoutesConstant } from '../../../constants/routes.constant';
import {
  abortAssistantAsk,
  loadAssistantHistoryThunk,
} from '../../../redux/Assistant/Assistant.thunk';
import {
  getAssistantAskOrigin,
  getAssistantIsLoading,
  getAssistantMessages,
  getAssistantMiniChatOpen,
} from '../../../redux/Assistant/Assistant.slice';
import AssistantReadyToast from './components/AssistantReadyToast.component';

const AssistantLifecycle = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { customToast } = useToast();
  const isLoading = useAppSelector(getAssistantIsLoading);
  const messages = useAppSelector(getAssistantMessages);
  const askOrigin = useAppSelector(getAssistantAskOrigin);
  const miniChatOpen = useAppSelector(getAssistantMiniChatOpen);
  const wasLoading = useRef(false);

  useEffect(() => {
    dispatch(loadAssistantHistoryThunk());

    const handleBeforeUnload = () => {
      abortAssistantAsk();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dispatch]);

  useEffect(() => {
    const finishedSuccessfully =
      wasLoading.current &&
      !isLoading &&
      messages[messages.length - 1]?.role === 'assistant' &&
      messages[messages.length - 1]?.status !== 'error';

    wasLoading.current = isLoading;

    if (!finishedSuccessfully || !askOrigin) {
      return;
    }

    const isAssistantPage = location.pathname === RoutesConstant.ASSISTANT;
    const isMiniChatVisible =
      location.pathname.startsWith(RoutesConstant.CANDIDATES) && miniChatOpen;

    if (isAssistantPage || isMiniChatVisible) {
      return;
    }

    const origin = askOrigin;

    customToast(({ id }: { id: string }) => (
      <AssistantReadyToast id={id} origin={origin} />
    ), {
      position: 'top-right',
      duration: 8000,
    });
  }, [
    askOrigin,
    customToast,
    isLoading,
    location.pathname,
    messages,
    miniChatOpen,
  ]);

  return null;
};

export default AssistantLifecycle;
